import * as React from 'react';

import type { PingAuth } from '@/api/ping-client';

// =============================================================================
// auth-context.tsx — État d'authentification SIMULÉ (OIDC stub, Option B).
// =============================================================================
// Le bearer et le tenant vivent en mémoire React (RULE_REF_006 : pas de
// localStorage pour données métier). En S0 c'est une identité simulée ; elle
// sera remplacée par le flux OIDC RS256 réel (Ory Hydra) sans changer ce
// contrat. L'UI métier ne fabrique JAMAIS le bearer elle-même : elle le lit ici.

/** Rôle de l'identité (gouverne la navigation role-gated). */
export type AuthRole = 'admin' | 'supplier';

/**
 * Rôle du contact FOURNISSEUR au sein du SIRET (MASTER vs collaborateur). Gouverne l'accès à la zone
 * « Mon équipe » (§3.7), strictement réservée au MASTER. PROVISOIRE : viendra de la session OIDC réelle.
 * Absent pour un admin. Le gating UI n'est PAS une frontière de sécurité : l'ACL réelle est côté API/RLS
 * (§3.7 MASTER-only serveur).
 */
export type SupplierContactRole = 'MASTER' | 'COLLABORATEUR';

export interface AuthState extends PingAuth {
  /**
   * Rôle de l'utilisateur — gouverne le menu de navigation (admin vs fournisseur).
   * PROVISOIRE (demo-auth) : sera renseigné par la session OIDC/magic-link réelle
   * (DV-OIDC-HYDRA-TEST). Optionnel : défaut 'admin' côté sidebar si absent.
   */
  role?: AuthRole;
  /**
   * Rôle du contact fournisseur (MASTER / collaborateur) — gouverne l'accès à « Mon équipe » (§3.7).
   * Renseigné uniquement pour un fournisseur ; absent pour un admin. Filtre UI (l'ACL est côté API).
   */
  supplierRole?: SupplierContactRole;
  /**
   * Sujet OIDC de l'ADMIN authentifié (identité RÉELLE renvoyée par GET /admin/auth/session, L-3),
   * AFFICHAGE SEUL (header). Absent pour un fournisseur ou un anonyme. Le gating UI n'est PAS une
   * frontière de sécurité : l'autorité reste l'AdminSessionGuard + le cookie httpOnly + le RLS. Ce
   * sujet vient TOUJOURS de la réponse serveur (autorité API), jamais fabriqué côté client.
   */
  subject?: string;
  /**
   * Nom d'affichage de l'UTILISATEUR authentifié (prénom + nom). AFFICHAGE SEUL.
   *   - ADMIN : résolu via l'annuaire admin (DEC-47), renvoyé par GET /admin/auth/session. Absent tant
   *     que l'IdP ne libère pas les claims `profile` ; le header retombe alors sur le `subject`.
   *   - FOURNISSEUR (P-6, 2026-09-23) : composé depuis GET /supplier/contacts/me, que le bootstrap
   *     appelle DÉJÀ pour le rôle. Notre propre base en est la source : aucune dépendance à l'IdP,
   *     contrairement au cas admin. Absent si le contact n'a ni nom ni prénom renseignés.
   * Dans les deux cas, TOUJOURS issu de la réponse serveur, jamais fabriqué côté client.
   */
  name?: string;
  /**
   * P-6 — RAISON SOCIALE de l'entreprise du FOURNISSEUR authentifié. AFFICHAGE SEUL (en-tête). Absente
   * pour un admin (il n'appartient à aucun tenant) et absente quand aucune source ne la porte : l'en-tête
   * retombe alors sur le SIRET, qui reste l'identité technique du tenant.
   *
   * DISTINCTE de `name`, et la distinction n'est pas cosmétique : `name` désigne une PERSONNE (en bas,
   * « qui suis-je »), `organisation` une ENTREPRISE (en haut, « pour qui je saisis »). Les confondre
   * dans un seul champ rendrait impossible d'afficher les deux, ce que l'écran doit précisément faire.
   */
  organisation?: string;
}

const AuthContext = React.createContext<AuthState | null>(null);

export interface AuthProviderProps {
  value: AuthState;
  children: React.ReactNode;
}

export function AuthProvider({ value, children }: AuthProviderProps): React.JSX.Element {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Lit l'identité authentifiée. Lève si aucun AuthProvider n'enveloppe l'arbre. */
export function useAuth(): AuthState {
  const ctx = React.useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth doit être utilisé dans un <AuthProvider>.');
  }
  return ctx;
}

/**
 * Capacité de RE-RÉSOLUTION de l'identité de session (S0-ROLE). Exposée par le bootstrap dynamique
 * (AuthBootstrap) : après l'ouverture d'une nouvelle session fournisseur (post-magic-link), l'appelant
 * déclenche `refresh()` pour que l'AuthState reflète le rôle 'supplier' AVANT de naviguer. Sur les
 * chemins statiques (tests, identité injectée), le défaut est un NO-OP : rien à re-résoudre.
 */
export interface AuthRefresh {
  refresh: () => Promise<void>;
}

const AuthRefreshContext = React.createContext<AuthRefresh>({
  refresh: async (): Promise<void> => {
    /* NO-OP : chemin statique (identité injectée) — aucune session réseau à re-résoudre. */
  },
});

export interface AuthRefreshProviderProps {
  value: AuthRefresh;
  children: React.ReactNode;
}

export function AuthRefreshProvider({
  value,
  children,
}: AuthRefreshProviderProps): React.JSX.Element {
  return (
    <AuthRefreshContext.Provider value={value}>
      {children}
    </AuthRefreshContext.Provider>
  );
}

/** Lit la capacité de re-résolution de session. Défaut NO-OP hors bootstrap dynamique. */
export function useAuthRefresh(): AuthRefresh {
  return React.useContext(AuthRefreshContext);
}

/**
 * Identité simulée par défaut pour le skeleton S0. Le tenant est un SIRET de
 * démonstration ; le bearer est un jeton opaque factice (aucun secret réel).
 */
export const demoAuthState: AuthState = {
  tenantId: '00000000000000',
  bearer: 'stub-oidc-bearer-s0',
  // PROVISOIRE : rôle admin par défaut (env test = tests administrateurs, S20). Le rôle
  // réel viendra de la session OIDC/magic-link (DV-OIDC-HYDRA-TEST).
  role: 'admin',
};

/**
 * Identité ANONYME : ni fournisseur ni admin (aucune session résolue au bootstrap). Rendue
 * UNIQUEMENT sur un chemin PUBLIC non authentifié (landing, CGU, onboarding) — JAMAIS sous l'AppShell
 * (donc aucun consommateur `useAuth` sensible : le chrome role-gated n'est pas monté). Le rôle est
 * INDÉFINI ; `tenantId`/`bearer` vides (pas de session). DISTINCT de `demoAuthState` (role:'admin',
 * réservé au chemin de test statique #3 de providers.tsx) : ne PAS réutiliser le SIRET sentinelle.
 */
export const anonymousAuthState: AuthState = {
  tenantId: '',
  bearer: '',
};
